import type { Editor as EditorArg } from "tinymce";
import { hasStructuralNestingErrors } from "./ssmlValidator";
import type { Notification } from "shared/models";
import { AlertColor } from "@mui/material";
import { stripEditorHTML } from "shared/utils/tts-editor-helper";
import { convertRawSSMLToTextFormat } from "./convertRawSSMLToTextFormat";
import {
  cleanWordSSML,
  stripSpeakTags,
  transformToSSML,
} from "shared/utils/ssml-helper";

export interface PastePreProcessEventLocal {
  content: string;
}

type SetNotificationFn = (
  updatedNotification: Omit<Partial<Notification>, "isOpen">
) => void;

export function pastePreprocessHandler(
  plugin: EditorArg,
  args: PastePreProcessEventLocal,
  setNotification?: SetNotificationFn,
  t?: (key: string) => string,
  isSSMLView?: boolean
) {
  const editor = plugin as EditorArg;
  args.content = editor.dom.decode(args.content);

  // Remove meta tags early
  args.content = args.content.replace(
    /<meta[^>]*>|<\/?html>|<\/?body>|<\/?head>/gi,
    ""
  );
  args.content = stripSpeakTags(args.content);

  // Pasting in SSML view
  if (isSSMLView) {
    const pastedContent = args.content;

    // Check if content contains SSML tags
    const ssmlTagPattern =
      /<(voice|prosody|emphasis|phoneme|say-as|break|style|lang)\s+[^>]*>.*?<\/\1>|<break\s+[^>]*\/?>|<(voice|prosody|emphasis|phoneme|say-as|style|lang)\s+[^>]*>/gis;
    const hasRawSSML = ssmlTagPattern.test(pastedContent);

    if (hasRawSSML) {
      // Only do basic structural validation
      if (hasStructuralNestingErrors(pastedContent)) {
        if (setNotification && t) {
          setNotification({
            severity: "error" as AlertColor,
            message: t("tts.customErrors.nestingErrors"),
          });
        }
        args.content = "";
        return;
      }
    }

    const { converted } = convertRawSSMLToTextFormat(pastedContent, true);
    const transformedContent = transformToSSML(converted, true);
    args.content = transformedContent;
    return;
  }

  // Non-SSML view processing
  args.content = stripEditorHTML(args.content);
  args.content = cleanWordSSML(args.content);

  // Check if content contains SSML tags
  const ssmlTagPattern =
    /<(voice|prosody|emphasis|phoneme|say-as|break|style|lang)\s+[^>]*>.*?<\/\1>|<break\s+[^>]*\/?>|<(voice|prosody|emphasis|phoneme|say-as|style|lang)\s+[^>]*>/gis;
  const hasRawSSML = ssmlTagPattern.test(args.content);

  if (hasRawSSML) {
    // Only do basic structural validation for paste
    if (hasStructuralNestingErrors(args.content)) {
      if (setNotification && t) {
        setNotification({
          severity: "error" as AlertColor,
          message: t("tts.validationErrorMessage"),
        });
      }
      args.content = "";
      return;
    }

    const { converted, hasInvalidTag } = convertRawSSMLToTextFormat(
      args.content,
      true
    );

    if (hasInvalidTag) {
      if (setNotification && t) {
        setNotification({
          severity: "error" as AlertColor,
          message: t("tts.validationErrorMessage"),
        });
      }
      args.content = "";
      return;
    }

    args.content = converted;
  } else {
    // Non-SSML cleanup
    args.content = args.content.replace(/<[^>]+>|\r|\n/g, (match) => {
      if (match.startsWith("<span") && match.includes("data-ssml"))
        return match;
      if (match.startsWith("<span") && match.includes("only-styling"))
        return match;
      if (["</span>", "<p>", "</p>"].includes(match)) return match;
      return "";
    });
  }
}
